import { z } from 'zod';
import { localeSchema } from './common';
import { deploymentSummarySchema } from './deployment';
import { membershipSchema } from './organizations';
import { permissionSchema } from './permissions';
import { platformPermissionSchema } from './platform';
import { projectSummarySchema } from './projects';
import { themeSchema } from './theme';

export const userStatusSchema = z.enum(['active', 'disabled', 'invited']);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const userSummarySchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string(),
  status: userStatusSchema,
  locale: localeSchema,
  roles: z.array(z.string()),
});
export type UserSummary = z.infer<typeof userSummarySchema>;

export const organizationSummarySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  defaultLocale: localeSchema,
  theme: themeSchema,
});

/**
 * The single payload the frontend needs to boot: who the user is, what they
 * may do, and how the application should look and read. Delivering it in one
 * response avoids a waterfall of three requests before the shell can render
 * (section 15.2).
 */
export const sessionContextSchema = z.object({
  user: userSummarySchema,
  organization: organizationSummarySchema,
  /**
   * The organization the user belongs to -- not the deployment above, which
   * `organization` has always meant here (docs/organizations-design.md).
   *
   * `isHost` false is what makes somebody an external member: the server has
   * already cut `permissions` down to what one may hold and refuses every
   * route not opened to them, so the UI reads this only to say whose data is
   * on screen and to leave out choices that could only be refused.
   */
  memberOf: membershipSchema,
  /**
   * The project this deployment is about, or null when none has been created
   * yet (architecture documentation section 2).
   *
   * The product monitors one infrastructure project for the people working on
   * it, so "which project?" is a property of the workspace and not a question
   * put to the reader on every screen. Resolving it once, server-side, and
   * shipping it in the payload the shell already waits for is what lets every
   * project-scoped page state its subject instead of asking for it: no picker,
   * no per-page list request, no loading state before the real content can
   * start.
   *
   * It is not gated on `project.read`. A reader who may see the schedule but
   * not the project record still needs the identifier every schedule request is
   * built from, and the name is what makes the screen legible; the endpoints
   * behind each page re-check their own permissions regardless.
   *
   * The seam for a future portfolio view is here rather than in the routes:
   * every API resource stays addressed by project id, so multi-project reading
   * becomes a choice this field records, not a re-shaping of the API.
   */
  project: projectSummarySchema.nullable(),
  /**
   * What this installation is: its lifecycle stage and the capabilities it was
   * deployed with (`deployment.ts`).
   *
   * The shell needs the stage to render a maintenance notice rather than an
   * empty product, and the enabled sets so the dashboard can drop a tile whose
   * module was switched off after the view was saved. Everything else that
   * follows from module selection needs no field here at all: `permissions`
   * below is already filtered to the enabled modules server-side, so the
   * navigation, the settings catalog and every `can()` in the frontend became
   * module-aware without learning what a module is.
   */
  deployment: deploymentSummarySchema,
  permissions: z.array(permissionSchema),
  /**
   * The provider plane (`platform.ts`), empty for every client user.
   *
   * Kept as its own array rather than merged into `permissions` so that a
   * frontend `can('...')` cannot accidentally be satisfied by a platform
   * grant, and so that "is this a provider account?" is one honest check
   * rather than a string prefix test.
   */
  platformPermissions: z.array(platformPermissionSchema),
  /** Double-submit token for state-changing requests (section 14.3). */
  csrfToken: z.string(),
  expiresAt: z.string(),
});
export type SessionContext = z.infer<typeof sessionContextSchema>;

export const authStatusSchema = z.discriminatedUnion('authenticated', [
  z.object({ authenticated: z.literal(false), loginUrl: z.string() }),
  z.object({ authenticated: z.literal(true), context: sessionContextSchema }),
]);
export type AuthStatus = z.infer<typeof authStatusSchema>;
